import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';

interface FeatureLockModalProps {
  title: string;
  description: string;
  benefits: string[];
  upgradeLabel: string;
  onUpgrade: () => void;
  onPricing: () => void;
  onClose: () => void;
}

export default function FeatureLockModal({
  title,
  description,
  benefits,
  upgradeLabel,
  onUpgrade,
  onPricing,
  onClose,
}: FeatureLockModalProps) {
  const { t } = useTranslation('dialogs');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feature-lock-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(17,24,39,0.38)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          background: 'var(--card-bg)',
          borderRadius: 18,
          border: '1px solid var(--acc-color-border)',
          boxShadow: '0 24px 80px rgba(17,24,39,0.18), 0 6px 24px rgba(17,24,39,0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '28px 30px 22px',
            background: 'var(--surface-secondary)',
            borderBottom: '1px solid var(--acc-color-border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '5px 10px',
                  borderRadius: 999,
                  background: 'var(--acc-badge-info-bg)',
                  color: 'var(--acc-badge-info-text)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  marginBottom: 14,
                }}
              >
                <span aria-hidden="true">🔒</span>
                {t('featureLock.lockedFeature')}
              </div>
              <h2
                id="feature-lock-modal-title"
                style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--acc-color-text)', letterSpacing: '-0.4px' }}
              >
                {title}
              </h2>
              <p style={{ margin: '10px 0 0', fontSize: 14, lineHeight: 1.6, color: 'var(--acc-color-text-muted)', maxWidth: 420 }}>
                {description}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--acc-color-text-muted)',
                fontSize: 22,
                lineHeight: 1,
                cursor: 'pointer',
              }}
              aria-label={t('featureLock.closeUpgradeModal')}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ padding: '22px 30px 28px' }}>
          <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
            {benefits.map((benefit) => (
              <div
                key={benefit}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid var(--acc-color-border)',
                  background: 'var(--surface-secondary)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: 'var(--acc-badge-info-bg)',
                    color: 'var(--acc-badge-info-text)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  +
                </span>
                <span style={{ color: 'var(--acc-color-text)', fontSize: 13, lineHeight: 1.6 }}>
                  {benefit}
                </span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <Button type="button" variant="secondary" onClick={onPricing}>
              {t('featureLock.viewFullPricing')}
            </Button>
            <Button type="button" onClick={onUpgrade}>
              {upgradeLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
